/**
 * Utility functions for filtering places and events based on timeline and categories
 */

import { combinePassageAndBookEvents } from './map-helpers';

export interface FilteredMapData {
    visiblePlaces: GeographicEntityWithRelevance[];
    visibleEvents: (HistoricalEventByPassage | HistoricalEvent)[];
    placeTypeCounts: Map<string, number>;
    eventCategoryCounts: Map<string, number>;
}

export interface FilterMapDataParams {
    placesByPassage: PlacesByPassage | null;
    historicalEventsByPassage: HistoricalEventsByPassage | null;
    historicalEventsByBook: HistoricalEventsByBook | null;
    generalPlacesAsEntities: GeographicEntityWithRelevance[];
    selectedYear: number;
    selectedPlaceTypes: Set<string>;
    selectedEventCategories: Set<string>;
    allHistoricalEvents: HistoricalEvent[] | null;
    passageEventsOnly: boolean;
}

/**
 * Filter places and events by timeline and categories, and calculate counts
 */
export function filterMapData(params: FilterMapDataParams): FilteredMapData {
    const {
        placesByPassage,
        historicalEventsByPassage,
        historicalEventsByBook,
        generalPlacesAsEntities,
        selectedYear,
        selectedPlaceTypes,
        selectedEventCategories,
        allHistoricalEvents,
        passageEventsOnly
    } = params;

    if (!placesByPassage) {
        return {
            visiblePlaces: [],
            visibleEvents: [],
            placeTypeCounts: new Map(),
            eventCategoryCounts: new Map()
        };
    }

    // Combine with PlacesByPassage entities (which already have importance field)
    const passageEntities: GeographicEntityWithRelevance[] = placesByPassage.entities.map(e => ({
        ...e,
        relevanceType: 'passage-place',
        relevanceReason: e.passageRelevance
    }));

    const allEntities: GeographicEntityWithRelevance[] = [...passageEntities, ...generalPlacesAsEntities];

    // Combine passage and book events
    const passageAndBookEvents = combinePassageAndBookEvents(historicalEventsByPassage, historicalEventsByBook);

    // Choose the event pool based on filter setting
    // If passageEventsOnly is true, only show passage/book events
    // If false, show all historical events available for this period
    const allEvents = passageEventsOnly
        ? passageAndBookEvents
        : (allHistoricalEvents || passageAndBookEvents);

    // Filter places by timeline - handle places without historical periods
    const placeMatchesTimeline = (entity: GeographicEntityWithRelevance) => {
        // If no historical context, always show (not time-bound)
        if (!entity.historicalContext || entity.historicalContext.length === 0) {
            return true;
        }

        // Check if any context overlaps with selected year
        return entity.historicalContext.some(ctx => selectedYear >= ctx.sortableYearStart && selectedYear <= ctx.sortableYearEnd);
    };

    const timeFilteredPlaces = allEntities.filter(entity => placeMatchesTimeline(entity));

    // Filter places by type
    const filteredPlaces = timeFilteredPlaces.filter(entity => selectedPlaceTypes.has(entity.type));

    // Filter events by timeline
    const timeFilteredEvents = allEvents.filter(event =>
        event.date.startYear <= selectedYear &&
        (event.date.endYear || event.date.startYear) >= selectedYear
    );

    // Filter events by category
    const filteredEvents = timeFilteredEvents.filter(event =>
        event.categories && event.categories.some(cat => selectedEventCategories.has(cat.category))
    );

    // Calculate counts for current timeline
    const placeTypeCounts = new Map<string, number>();
    timeFilteredPlaces.forEach(entity => {
        placeTypeCounts.set(entity.type, (placeTypeCounts.get(entity.type) || 0) + 1);
    });

    const eventCategoryCounts = new Map<string, number>();
    timeFilteredEvents.forEach(event => {
        if (event.categories) {
            event.categories.forEach(cat => {
                eventCategoryCounts.set(cat.category, (eventCategoryCounts.get(cat.category) || 0) + 1);
            });
        }
    });

    return {
        visiblePlaces: filteredPlaces,
        visibleEvents: filteredEvents,
        placeTypeCounts,
        eventCategoryCounts
    };
}
